import React from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { useTranslation } from "react-i18next";
import ResponsiveDialogManager from "./ResponsiveDialogManager";
import PreviewPlayer from "../previewPlayer/PreviewPlayer";
import { Typography } from "@mui/material";

const PREFIX = "ConfirmationDialog";

const classes = {
  linkColor: `${PREFIX}-linkColor`,
  dialogMenuStyle: `${PREFIX}-dialogMenuStyle`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.linkColor}`]: {
    color: theme.palette.primary.main,
    cursor: "pointer",
  },

  [`& .${classes.dialogMenuStyle}`]: {
    backgroundColor: "#424242",
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ConfirmationDialog(props) {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const [previewDocument, setPreviewDocument] = React.useState(null);

  React.useEffect(() => {
    if (props.element) {
      setName(
        props.mode === "deleteTheme"
          ? props.element.title
          : props.mode === "deleteUser"
          ? props.element.email
          : props.mode === "deleteTarget" || props.mode === "deleteZone"
          ? props.element.label
          : props.mode === "deleteCategory" ||
            props.mode === "deleteEntity" ||
            props.mode === "deleteModel"
          ? props.element.name
          : props.mode === "configurator" ||
            props.mode === "customSave" ||
            props.mode === "QrCodes"
          ? props.element
          : props.element.displayName
      );
    }
  }, [props.element]);

  const handleClose = (e, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return false;
    } else {
      if (props.customClose) {
        props.customClose();
      }
      props.setOpen(false);
      if (e) e.stopPropagation();
    }
  };

  const onAgreeClose = (e, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return false;
    } else {
      props.setOpen(false);
      if (e) e.stopPropagation();
    }
  };

  const onAgree = (e) => {
    e.stopPropagation();
    props.onAgree();
    onAgreeClose();
  };

  const texts = {
    deleteTarget: {
      delete: t("myDesign.informations.delete"),
      areYouSure: t("myDesign.informations.areYouSure"),
      alertDelete: t("myDesign.informations.alertDelete"),
      accept: t("myDesign.informations.accept"),
      reject: t("myDesign.informations.reject"),
    },
    deleteZone: {
      delete: t("myDesign.informations.delete"),
      areYouSure: t("myDesign.informations.areYouSureZone"),
      alertDelete: t("myDesign.informations.alertDeleteZone"),
      accept: t("myDesign.informations.accept"),
      reject: t("myDesign.informations.reject"),
    },
    customSave: {
      delete: t("myDesign.informations.quitConfiguration"),
      areYouSure: t("myDesign.informations.leavingWithoutSave"),
      alertDelete: t("myDesign.informations.saveAlert"),
      accept: t("myDesign.informations.save"),
      reject: t("myDesign.informations.Continue"),
    },
    deleteExparience: {
      delete: t("myExpariences.delete"),
      areYouSure: t("myExpariences.areYouSure"),
      alertDelete: t("myExpariences.alertDelete"),
      accept: t("myExpariences.accept"),
      reject: t("myExpariences.reject"),
    },
    deleteUser: {
      delete: t("users.delete"),
      areYouSure: t("users.areYouSure"),
      alertDelete: t("users.alertDelete"),
      accept: t("users.accept"),
      reject: t("users.reject"),
    },
    deleteEntity: {
      delete: t("entities.delete"),
      areYouSure: t("entities.areYouSure"),
      alertDelete: t("entities.alertDelete"),
      accept: t("entities.accept"),
      reject: t("entities.reject"),
    },
    deleteModel: {
      delete: t("modelsManager.delete"),
      areYouSure: t("modelsManager.areYouSure"),
      alertDelete: t("modelsManager.alertDelete"),
      accept: t("modelsManager.accept"),
      reject: t("modelsManager.reject"),
    },
    deleteTheme: {
      delete: t("modelsManager.delete"),
      areYouSure: t("modelsManager.areYouSureTheme"),
      alertDelete: t("modelsManager.alertDeleteTheme"),
      accept: t("modelsManager.accept"),
      reject: t("modelsManager.reject"),
    },
    deleteCategory: {
      delete: t("categories.delete"),
      areYouSure: t("categories.areYouSure"),
      alertDelete: t("categories.alertDelete"),
      accept: t("categories.accept"),
      reject: t("categories.reject"),
    },
    resetStat: {
      delete: t("myDesign.stats.delete"),
      areYouSure: t("myDesign.stats.areYouSure"),
      alertDelete: t("myDesign.stats.alertDelete"),
      accept: t("myDesign.stats.accept"),
      reject: t("myDesign.stats.reject"),
    },
    configurator: {
      delete: t("configurator.delete"),
      areYouSure: t("configurator.areYouSure"),
      alertDelete: t("configurator.alertDelete"),
      accept: t("configurator.accept"),
      reject: t("configurator.reject"),
    },
    QrCodes: {
      delete: t("QrManager.delete"),
      areYouSure: t("QrManager.areYouSure"),
      alertDelete: t("QrManager.alertDelete"),
      accept: t("QrManager.accept"),
      reject: t("QrManager.reject"),
    },
    termsOfUseDataProtection: {
      touAndDataProtection: t("oauth.confirm.touAndDataProtection"),
      confirmText: t("oauth.confirm.confirmText"),
      tou: t("oauth.confirm.tou"),
      and: t("oauth.confirm.and"),
      dataProtection: t("oauth.confirm.dataProtection"),
      accept: t("categories.accept"),
      reject: t("categories.reject"),
    },
    publish: {
      title: props.isActive
        ? t("exparienceConfirmationDialog.unpublishTitle")
        : t("exparienceConfirmationDialog.publishTitle"),
      explanation: props.isActive
        ? t("exparienceConfirmationDialog.unpublishExplanation")
        : t("exparienceConfirmationDialog.publishExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    visible: {
      title: props.isActive
        ? t("exparienceConfirmationDialog.invisibleTitle")
        : t("exparienceConfirmationDialog.visibleTitle"),
      explanation: props.isActive
        ? t("exparienceConfirmationDialog.invisibleExplanation")
        : t("exparienceConfirmationDialog.visibleExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    notify: {
      title: t("exparienceConfirmationDialog.notifyTitle"),
      explanation: t("exparienceConfirmationDialog.notifyExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    duplicate: {
      title: t("exparienceConfirmationDialog.duplicateTitle"),
      explanation: t("exparienceConfirmationDialog.duplicateExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    promote: {
      title: t("exparienceConfirmationDialog.promoteTitle"),
      explanation: t("exparienceConfirmationDialog.promoteExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    unpromote: {
      title: t("exparienceConfirmationDialog.unPromoteTitle"),
      explanation: t("exparienceConfirmationDialog.unPromoteExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
    deleteSurvey: {
      title: t("exparienceConfirmationDialog.deleteSurvey"),
      explanation: t("exparienceConfirmationDialog.deleteSurveyExplanation"),
      accept: t("exparienceConfirmationDialog.accept"),
      reject: t("exparienceConfirmationDialog.reject"),
    },
  };

  return (
    <Root>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        classes={{ paper: classes.dialogMenuStyle }}
      >
        {props.mode === "termsOfUseDataProtection" ? (
          <React.Fragment>
            <ResponsiveDialogManager
              fullScreen
              title={
                !previewDocument
                  ? ""
                  : previewDocument.includes("pc.pdf")
                  ? t("account.dataProtection")
                  : t("account.termsOfUse")
              }
              open={!previewDocument ? false : true}
              onClose={() => setPreviewDocument(null)}
              renderer={
                <PreviewPlayer
                  media={{ content: previewDocument, type: "document" }}
                />
              }
            />
            <DialogTitle id="alert-dialog-slide-title">
              <Typography variant="body1">
                {texts[props.mode]["touAndDataProtection"]}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                {texts[props.mode]["confirmText"]}
                <span
                  onClick={() =>
                    setPreviewDocument(
                      "https://cg-ads.s3.fr-par.scw.cloud/cgu.pdf"
                    )
                  }
                  className={classes.linkColor}
                >
                  {texts[props.mode]["tou"]}
                </span>
                {texts[props.mode]["and"]}
                <span
                  onClick={() =>
                    setPreviewDocument(
                      "https://cg-ads.s3.fr-par.scw.cloud/pc.pdf"
                    )
                  }
                  className={classes.linkColor}
                >
                  {texts[props.mode]["dataProtection"]}
                </span>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => handleClose(e)} color="primary">
                {texts[props.mode]["reject"]}
              </Button>
              <Button onClick={(e) => onAgree(e)} color="primary">
                {texts[props.mode]["accept"]}
              </Button>
            </DialogActions>
          </React.Fragment>
        ) : name ? (
          <React.Fragment>
            <DialogTitle id="alert-dialog-slide-title">
              <Typography variant="body1">
                {texts[props.mode]["delete"]} {name} ?
              </Typography>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                {texts[props.mode]["areYouSure"]}
                {name}
                <br />
                {texts[props.mode]["alertDelete"]}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => handleClose(e)} color="primary">
                {texts[props.mode]["reject"]}
              </Button>
              <Button onClick={(e) => onAgree(e)} color="primary">
                {texts[props.mode]["accept"]}
              </Button>
            </DialogActions>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DialogTitle id="alert-dialog-slide-title">
              <Typography variant="body1">
                {texts[props.mode]["title"]} ?
              </Typography>
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-slide-description">
                {texts[props.mode]["explanation"]}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => handleClose(e)} color="primary">
                {texts[props.mode]["reject"]}
              </Button>
              <Button onClick={(e) => onAgree(e)} color="primary">
                {texts[props.mode]["accept"]}
              </Button>
            </DialogActions>
          </React.Fragment>
        )}
      </Dialog>
    </Root>
  );
}
